import React, { useState, useEffect } from "react";
import { CreditCard } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { usePaymentTheme } from "../home/respayment/hooks/usePaymentTheme";
import OrderTabs from "./components/OrderTabs";
import OrderActions from "./components/OrderActions";
import OrderCard from "./components/OrderCard";
import { useRunningOrders } from "./hooks/useRunningOrders";
import { useOrderStats } from "./hooks/useOrderStats";
import { PageTitle } from "src/shared/PageTitle";
import { motion, AnimatePresence } from "framer-motion";

const ResRunningOrder: React.FC = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [activeTab, setActiveTab] = useState<"pending">("pending"); // Only pending tab
  const [showFilters, setShowFilters] = useState(false);

  const { orders, pagination, loading, fetchOrders, setPagination, handleRefresh } = useRunningOrders();
  const orderStats = useOrderStats(orders);
  const { fontClass, themeBorderColor, themeTextColor, themeRingColor, getBgClass, getLightBgClass } = usePaymentTheme();

  const handleAddOrder = () => navigate("/v1/Reshome");

  const handlePay = (orderId: string) => {
    if (!orderId) {
      toast.error("Invalid order ID");
      return;
    }
    navigate(`/v1/Respayment/${orderId}`);
  };

  // In ResRunningOrder component, update the handleEdit function:
  const handleEdit = (id: string) => {
    const orderToEdit = orders.find((order) => order._id?.$oid === id);
    if (orderToEdit) {
      // Ensure products have the correct structure
      const enhancedOrder = {
        ...orderToEdit,
        Products: orderToEdit.Products.map((p: any) => ({
          ...p,
          // Ensure productId is set for matching
          productId: p._id?.$oid || p.productId,
        })),
      };

      navigate("/v1/Reshome", {
        state: {
          editOrder: enhancedOrder,
        },
      });
    }
  };

  useEffect(() => {
    document.title = "Running Order › iMetanic";
    const delay = setTimeout(() => {
      const size = search ? 12 : pagination.pageSize;
      setPagination((prev) => ({ ...prev, page: 1, pageSize: size }));
      // Always fetch with "pending" status
      fetchOrders(1, size, { search, status: "pending" });
    }, 400);
    return () => clearTimeout(delay);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]); // Remove activeTab from dependencies since it's always "pending"

  return (
    <div className={`flex flex-col h-[91.5vh] bg-[#F8F9FA] to-gray-75 ${fontClass}`}>
      <PageTitle title="- Running Order" />
      <main className="flex-1 p-1 lg:p-2 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent gap-1 h-[91.5vh] overflow-auto">
        <div className="bg-white rounded-2xl p-2 shadow-xs border border-gray-100 mb-2.5">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
            <div className="flex-1">
              <OrderTabs
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                orderStats={orderStats}
                totalRecords={pagination.totalRecords}
                getBgClass={getBgClass}
                themeRingColor={themeRingColor}
                themeTextColor={themeTextColor}
              />
            </div>
            <div className="flex-shrink-0">
              <OrderActions
                search={search}
                setSearch={setSearch}
                showFilters={showFilters}
                setShowFilters={setShowFilters}
                loading={loading}
                handleAddOrder={handleAddOrder}
                handleRefresh={handleRefresh}
                themeBorderColor={themeBorderColor}
                themeRingColor={themeRingColor}
                getBgClass={getBgClass}
              />
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="text-center">
              <div className={`animate-spin rounded-full h-12 w-12 border-b-2 ${themeBorderColor} mx-auto mb-4`}></div>
              <p className="text-gray-600">Loading orders...</p>
            </div>
          </div>
        ) : orders.length === 0 ? (
          <div className="bg-white rounded-2xl p-12 text-center shadow-xs border border-gray-100">
            <div className="max-w-md mx-auto">
              <CreditCard className="w-20 h-20 text-gray-300 mx-auto mb-6" />
              <h3 className="text-xl font-semibold text-gray-900 mb-3">No pending orders</h3>
              <p className="text-gray-500 mb-6">{search ? "No pending orders match your search criteria." : "There are no pending orders at the moment."}</p>
              {!search && (
                <button
                  onClick={handleAddOrder}
                  className={`bg-gradient-to-r ${getBgClass()} hover:opacity-90 text-white font-semibold px-8 py-3 rounded-xl transition-all duration-200 transform hover:scale-105 shadow-lg ${themeRingColor.replace(
                    "ring",
                    "shadow"
                  )}/25`}
                >
                  Create New Order
                </button>
              )}
            </div>
          </div>
        ) : (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-3 gap-4 mb-6">
              <AnimatePresence>
                {orders.map((order) => {
                  // In ResRunningOrder component, update the normalizedOrder logic:
                  const normalizedOrder = {
                    ...order,
                    Orderid: order.Orderid || order._id?.$oid, // Make sure Orderid is available
                    totalAmount: order.totalAmount ?? "0.00",
                    discount: order.discount ?? 0,
                    deliveryFee: order.deliveryFee ?? "0.00",
                    Finalamount: order.Finalamount ?? "0.00",

                    // Transform Products to match the expected structure
                    Products: order.Products.map((p) => {
                      // Extract productId from the product object if it exists
                      const productId = p._id?.$oid;

                      return {
                        ...p,
                        productId: productId, // Ensure productId is included
                        _id: p._id ? { $oid: productId } : undefined, // Keep original structure if needed
                        productName: p.productName || "N/A",
                        amount: Number(p.amount),
                        quantity: p.quantity || 0,
                        notes: p.notes || "", // Include notes for modification tracking
                      };
                    }),
                  };

                  return (
                    <motion.div key={order._id.$oid} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}>
                      <OrderCard
                        key={order._id.$oid}
                        order={normalizedOrder}
                        getLightBgClass={getLightBgClass}
                        getBgClass={getBgClass}
                        themeTextColor={themeTextColor}
                        themeBorderColor={themeBorderColor}
                        handleEdit={handleEdit}
                        handlePay={handlePay}
                      />
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </motion.div>

            {/* Fixed pagination display logic */}
            {pagination.totalPages > 1 && pagination.totalPages > 0 && (
              <div className="flex justify-end items-center gap-3 mt-8 mr-4">
                <button
                  onClick={() => fetchOrders(pagination.page - 1, pagination.pageSize, { search, status: "pending" })}
                  disabled={pagination.page <= 1}
                  className="px-6 py-3 bg-white border border-gray-300 rounded-xl text-base font-medium text-gray-800 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-sm hover:shadow-md min-w-[100px]"
                >
                  Previous
                </button>
                <span className="px-5 py-3 text-base font-medium text-gray-800 bg-white border border-gray-200 rounded-xl shadow-sm min-w-[120px] text-center">
                  Page {pagination.page} of {pagination.totalPages}
                </span>
                <button
                  onClick={() => fetchOrders(pagination.page + 1, pagination.pageSize, { search, status: "pending" })}
                  disabled={pagination.page >= pagination.totalPages}
                  className="px-6 py-3 bg-white border border-gray-300 rounded-xl text-base font-medium text-gray-800 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-sm hover:shadow-md min-w-[100px]"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </main>
      <ToastContainer autoClose={1000} />
    </div>
  );
};

export default ResRunningOrder;
